import { useState, useEffect } from "react";
import { X, MessageCircle, Download, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

interface ScrollCTAConfig {
  id: string;
  triggerSelector: string;
  icon: React.ReactNode;
  message: string;
  actionLabel: string;
  actionHref: string;
}

const ctaConfigs: ScrollCTAConfig[] = [
  {
    id: "services-cta",
    triggerSelector: "#services-section",
    icon: <MessageCircle className="w-4 h-4" />,
    message: "Need help choosing the right service?",
    actionLabel: "Talk to our team",
    actionHref: "/contact",
  },
  {
    id: "products-cta",
    triggerSelector: "#products-section",
    icon: <Download className="w-4 h-4" />,
    message: "Want the full product catalog?",
    actionLabel: "Download PDF",
    actionHref: "/contact?subject=Product Catalog Request",
  },
  {
    id: "footer-cta",
    triggerSelector: "footer",
    icon: <Phone className="w-4 h-4" />,
    message: "Before you go —",
    actionLabel: "Get a free fleet consultation",
    actionHref: "/contact?subject=Fleet Consultation",
  },
];

const ScrollCTA = () => {
  const [activeCTA, setActiveCTA] = useState<string | null>(null);
  const [dismissedCTAs, setDismissedCTAs] = useState<Set<string>>(new Set());
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/") return;

    const observers: IntersectionObserver[] = [];

    ctaConfigs.forEach((config) => {
      const el = document.querySelector(config.triggerSelector);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !dismissedCTAs.has(config.id)) {
            setActiveCTA(config.id);
          } else if (!entry.isIntersecting && activeCTA === config.id) {
            setActiveCTA(null);
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [location.pathname, dismissedCTAs, activeCTA]);

  const activeConfig = ctaConfigs.find((c) => c.id === activeCTA);

  const handleDismiss = (id: string) => {
    setDismissedCTAs((prev) => new Set(prev).add(id));
    setActiveCTA(null);
  };

  return (
    <AnimatePresence>
      {activeConfig && (
        <motion.div
          key={activeConfig.id}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 glass-vibrant rounded-full px-5 py-2.5 shadow-lg flex items-center gap-3"
        >
          {activeConfig.icon}
          <span className="text-sm text-foreground font-medium">{activeConfig.message}</span>
          <Link
            to={activeConfig.actionHref}
            className="text-sm font-semibold text-primary hover:underline whitespace-nowrap"
          >
            {activeConfig.actionLabel}
          </Link>
          <button onClick={() => handleDismiss(activeConfig.id)} className="p-0.5 hover:bg-secondary/50 rounded-full ml-1">
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollCTA;
